import { useCallback, useEffect, useMemo, useState } from "react";
import ComboBoxFormik from "../../../../../common/components/form/ComboBoxFormik";
import { useFormikContext } from "formik";
import { numberToCurrency } from "../../../../../common/helpers/currency.helpers";
import InputFormik from "../../../../../common/components/form/InputFormik";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import InputMoney from "@/common/components/form/CurrencyInputFormik";

type EasyDepositEntryOption = {
  value: string;
  label: string;
};

interface EasyDepositFormikProps {
  easyDeposit: boolean;
  setEasyDeposit: (value: boolean) => void;
}

export function EasyDepositFormik({ easyDeposit, setEasyDeposit }: EasyDepositFormikProps) {
  const formik = useFormikContext();
  const [easyDepositEntryValue, setEasyDepositEntryValue] = useState(400); // ✅ Valor padrão: R$ 400

  const EASY_DEPOSIT_TOTAL = 800;
  const MIN_ENTRY_VALUE = 250;
  const MAX_ENTRY_VALUE = 400;
  const INSTALLMENT_VALUE = 50;
  const FIXED_INSTALLMENT_COUNT = 8;

  const SPECIAL_OPTION_VALUE = 600;

  // Calcula as opções de entrada para o Caução Facilitado
  const easyDepositEntryOptions = useMemo<EasyDepositEntryOption[]>(() => {
    const options: EasyDepositEntryOption[] = [];

    // Opções tradicionais (8 parcelas fixas)
    for (let value = MIN_ENTRY_VALUE; value <= MAX_ENTRY_VALUE; value += 50) {
      const installments = FIXED_INSTALLMENT_COUNT;
      options.push({
        value: value.toString(),
        label: `Entrada: R$ ${numberToCurrency(value.toFixed(2))} + ${installments}x R$ ${numberToCurrency(INSTALLMENT_VALUE.toFixed(2))}`
      });
    }

    // ✅ Nova opção especial
    options.push({
      value: SPECIAL_OPTION_VALUE.toString(),
      label: `Entrada: R$ ${numberToCurrency("300")} + 2x R$ ${numberToCurrency("50")} (Especial)`
    });

    return options;
  }, [MIN_ENTRY_VALUE, MAX_ENTRY_VALUE, FIXED_INSTALLMENT_COUNT, INSTALLMENT_VALUE, SPECIAL_OPTION_VALUE]);

  const easyDepositCalculations = useMemo(() => {
    // ✅ Garante que easyDepositEntryValue é válido - usa 400 como padrão
    const entryValue = isNaN(easyDepositEntryValue) || easyDepositEntryValue <= 0 ? 400 : easyDepositEntryValue;

    // ✅ Verificar se é a opção especial (1x 300 + 2x 150)
    if (entryValue === SPECIAL_OPTION_VALUE) {
      return {
        entryValue: 300,
        remainingValue: 300, // 2x 150 = 300
        installmentCount: 2,
        installmentValue: 50,
        totalValue: 600 // 300 + 300
      };
    }

    // ✅ Opções tradicionais: Sempre 8 parcelas fixas de R$ 50
    const installmentCount = FIXED_INSTALLMENT_COUNT;
    const totalInstallmentValue = installmentCount * INSTALLMENT_VALUE; // 8 x 50 = 400
    const totalValue = entryValue + totalInstallmentValue; // entrada + 400

    return {
      entryValue,
      remainingValue: totalInstallmentValue, // Sempre R$ 400 em parcelas
      installmentCount,
      installmentValue: INSTALLMENT_VALUE,
      totalValue // Varia conforme a entrada (300+400=700, 350+400=750, etc.)
    };
  }, [easyDepositEntryValue, FIXED_INSTALLMENT_COUNT, INSTALLMENT_VALUE, SPECIAL_OPTION_VALUE]);

  useEffect(() => {
    if (easyDeposit) {
      const calculations = easyDepositCalculations;

      formik.setFieldValue('easyDepositEntryValue', calculations.entryValue);
      formik.setFieldValue('easyDepositInstallments', calculations.installmentCount);
      formik.setFieldValue('easyDepositRemainingValue', calculations.remainingValue);
    }
    if (easyDeposit && !formik.getFieldProps('easyDepositEntryValue').value) {
      setEasyDepositEntryValue(400); // ✅ Valor padrão: R$ 400

      const calculations = easyDepositCalculations;

      formik.setFieldValue('easyDepositEntryValue', calculations.entryValue);
      formik.setFieldValue('easyDepositInstallments', calculations.installmentCount);
      formik.setFieldValue('easyDepositRemainingValue', calculations.remainingValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [easyDeposit, easyDepositEntryValue, MIN_ENTRY_VALUE, FIXED_INSTALLMENT_COUNT]);

  const handleEasyDepositChange = () => {
    if (easyDeposit) {
      // Desabilitar Caução Facilitado
      setEasyDepositEntryValue(400);
      formik.setFieldValue('deposits', 0);
      formik.setFieldValue('installmentOption', '');
      formik.setFieldValue('methodPayment', '');
      formik.setFieldValue('easyDeposit', false);
      formik.setFieldValue('easyDepositEntryValue', 0);
      formik.setFieldValue('easyDepositInstallments', 0);
      formik.setFieldValue('easyDepositInstallmentsValue', 0);
      formik.setFieldValue('easyDepositDate', '');
      setEasyDeposit(false);
    } else {
      // Habilitar Caução Facilitado
      const calculations = easyDepositCalculations;
      formik.setFieldValue('deposit', 0); // ✅ Usa o total calculado
      formik.setFieldValue('methodPayment', 'PIX');
      formik.setFieldValue('easyDeposit', true);
      formik.setFieldValue('easyDepositEntryValue', calculations.entryValue);
      formik.setFieldValue('easyDepositInstallments', calculations.installmentCount);
      formik.setFieldValue('easyDepositInstallmentsValue', calculations.installmentValue);
      formik.setFieldValue('easyDepositRemainingValue', calculations.remainingValue);
      setEasyDeposit(true);
    }
  }

  const handleEasyDepositEntryChange = useCallback((selectedOption: unknown) => {

    let newEntryValue: number;

    if (typeof selectedOption === 'string') {
      newEntryValue = parseInt(selectedOption);
    } else {
      const option = selectedOption as EasyDepositEntryOption;
      newEntryValue = parseInt(option.value);
    }

    if (isNaN(newEntryValue)) {
      console.error('Valor inválido recebido:', selectedOption);
      return;
    }
    setEasyDepositEntryValue(newEntryValue);

    // ✅ Usar calculations para obter os valores corretos
    const tempCalculations = newEntryValue === SPECIAL_OPTION_VALUE
      ? {
        entryValue: 300,
        remainingValue: 300,
        installmentCount: 2,
        installmentValue: 50,
        totalValue: 600
      }
      : {
        entryValue: newEntryValue,
        remainingValue: FIXED_INSTALLMENT_COUNT * INSTALLMENT_VALUE,
        installmentCount: FIXED_INSTALLMENT_COUNT,
        installmentValue: INSTALLMENT_VALUE,
        totalValue: newEntryValue + (FIXED_INSTALLMENT_COUNT * INSTALLMENT_VALUE)
      };

    formik.setFieldValue('easyDepositEntryValue', tempCalculations.entryValue);
    formik.setFieldValue('easyDepositInstallments', tempCalculations.installmentCount);
    formik.setFieldValue('easyDepositRemainingValue', tempCalculations.remainingValue);
  }, [formik, FIXED_INSTALLMENT_COUNT, INSTALLMENT_VALUE, SPECIAL_OPTION_VALUE]);

  if (!easyDeposit) {
    return (
      <div className="relative color flex items-center mb-4">
        <label htmlFor={`easyDeposit`} className="block text-sm font-medium text-gray-600">
          <div className="flex gap-2 items-center justify-start cursor-pointer">
            <ImCheckboxUnchecked />
            <span>Caução Facilitado (R$ {numberToCurrency(EASY_DEPOSIT_TOTAL.toFixed(2))})</span>
          </div>
        </label>
        <input
          id={`easyDeposit`}
          name={`easyDeposit`}
          type="checkbox"
          checked={easyDeposit}
          onChange={handleEasyDepositChange}
          className="border p-2 rounded w-full absolute opacity-0 cursor-pointer"
        />
      </div>
    );
  }

  return (
    <div>
      {/* Checkbox para habilitar/desabilitar */}
      <div className="relative color flex items-center mb-4">
        <label htmlFor={`easyDeposit`} className="block text-sm font-medium text-gray-600">
          <div className="flex gap-2 items-center justify-start text-green cursor-pointer">
            <ImCheckboxChecked />
            <span>Caução Facilitado (R$ {numberToCurrency(EASY_DEPOSIT_TOTAL.toFixed(2))})</span>
          </div>
        </label>
        <input
          id={`easyDeposit`}
          name={`easyDeposit`}
          type="checkbox"
          checked={easyDeposit}
          onChange={handleEasyDepositChange}
          className="border p-2 rounded w-full absolute opacity-0 cursor-pointer"
        />
      </div>

      {/* Configuração do Caução Facilitado */}
      <div className="mt-4 p-4 border border-green-200 rounded-lg bg-green-50">
        <h3 className="text-lg font-semibold text-green-800 mb-4">
          Configuração do Caução Facilitado
        </h3>

        {/* Resumo do Caução Facilitado */}
        <div className="bg-white p-4 rounded-lg border border-green-200 mb-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Valor Total:</span>
              <span className="ml-2 font-bold text-green-600">
                R$ {numberToCurrency(easyDepositCalculations.totalValue.toFixed(2))}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Valor da Entrada:</span>
              <span className="ml-2 font-bold text-blue-600">
                R$ {numberToCurrency(easyDepositCalculations.entryValue.toFixed(2))}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Valor Restante:</span>
              <span className="ml-2 font-bold text-orange-600">
                R$ {numberToCurrency(easyDepositCalculations.remainingValue.toFixed(2))}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Parcelamento:</span>
              <span className="ml-2 font-bold text-purple-600">
                {easyDepositCalculations.installmentCount}x R$ {numberToCurrency(easyDepositCalculations.installmentValue.toFixed(2))}
              </span>
            </div>
          </div>
        </div>

        {/* Seletor de valor de entrada */}
        <ComboBoxFormik
          className="mb-4"
          name="easyDepositEntryOption"
          label="Escolha o valor da entrada:"
          options={easyDepositEntryOptions}
          required={true}
          onChange={handleEasyDepositEntryChange}
          value={easyDepositEntryValue.toString()} // ✅ Usa o valor diretamente
        />

        {/* Campo oculto para armazenar o valor total do caução */}
        <InputMoney
          className="hidden"
          name="depositInstallments"
          label="Valor do Caução"
          required={true}
        />

        {/* Data de cobrança */}
        <InputFormik
          className="mt-4"
          label="Data de cobrança da primeira parcela - Caução"
          name={"easyDepositDate"}
          type="date"
          required={true}
          placeholder="dd/mm/aaaa"
        />

        {/* Informações adicionais */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">
                Informações do Caução Facilitado
              </h4>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Valor fixo de R$ {numberToCurrency(EASY_DEPOSIT_TOTAL.toFixed(2))}</li>
                  <li>Entrada flexível de R$ {numberToCurrency(MIN_ENTRY_VALUE.toFixed(2))} a R$ {numberToCurrency(MAX_ENTRY_VALUE.toFixed(2))}</li>
                  <li>Parcelas fixas de R$ {numberToCurrency(INSTALLMENT_VALUE.toFixed(2))}</li>
                  <li>Pagamento via PIX (sem taxas adicionais)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
